import { Mapping } from 'api/MappingsAPI';
import { FIELD_TABLE_LAYOUT_WITH_BULK_ACTIONS } from 'modules/dataDictionary/constants';
import { FieldProperty, ModalAction } from 'modules/dataDictionary/types';
import { formatAncestorAndFieldName } from 'modules/dataDictionary/utils';
import { FieldType } from 'modules/domains';
import styled from 'styled-components';
import { Checkbox } from 'ui/checkboxV2';
import { GridRow } from 'ui/indexPages/tables/grid';
import { useModalContext } from 'utils/contexts/modal';
import { Cell } from './Cell';
import { CreateFieldCell } from './CreateFieldCell';
import { DateCreatedCell } from './DateCreatedCell';
import { EditableCell } from './EditableCell';
import { FieldNameCell } from './FieldNameCell';
import { VisibilityCell } from './VisibilityFieldCell';

const StyledGridRow = styled(GridRow)`
  align-items: flex-start;
`;

type Props = {
  field: Mapping;
  fieldIds?: string[];
  gridLayout: string;
  isBulkActionEnabled?: boolean;
  objectFieldNames: string[];
  onCreateNewField: (fieldName: string) => void;
  onSelectRow: (fieldName: string) => void;
};

export const TableRow = ({
  field,
  fieldIds = [],
  gridLayout = FIELD_TABLE_LAYOUT_WITH_BULK_ACTIONS,
  isBulkActionEnabled = true,
  objectFieldNames,
  onCreateNewField,
  onSelectRow,
}: Props) => {
  const { showModal } = useModalContext();

  const {
    createdAt,
    description = '',
    fieldName,
    fieldType,
    isHidden,
    isSystemField = false,
    sampleValue = '',
  } = field;

  const isObjectFieldType = fieldType === FieldType.OBJECT;
  const [ancestor, finalFieldName] = formatAncestorAndFieldName(
    objectFieldNames,
    fieldName,
    fieldType,
  );

  return (
    <StyledGridRow gridLayout={gridLayout} key={fieldName}>
      {isBulkActionEnabled && (
        <Cell>
          <Checkbox
            isDisabled={false}
            id={fieldName}
            label=""
            status={fieldIds.includes(fieldName)}
            setChecked={() => onSelectRow(fieldName)}
          />
        </Cell>
      )}
      <FieldNameCell ancestor={ancestor} name={finalFieldName} />
      <EditableCell
        placeholder="Add a description"
        value={description}
        onClick={() =>
          showModal({
            action: ModalAction.EDIT_PROPERTY,
            payload: {
              field,
              fieldProperty: FieldProperty.DESCRIPTION,
            },
          })
        }
      />
      <EditableCell
        placeholder="Add a sample value"
        value={sampleValue}
        onClick={() =>
          showModal({
            action: ModalAction.EDIT_PROPERTY,
            payload: {
              field,
              fieldProperty: FieldProperty.SAMPLE_VALUE,
            },
          })
        }
      />
      {isBulkActionEnabled && <VisibilityCell isHidden={Boolean(isHidden)} />}
      <Cell>{fieldType}</Cell>
      <DateCreatedCell isSystemField={isSystemField} timestamp={createdAt} />
      <CreateFieldCell
        isShown={isObjectFieldType && !isSystemField}
        onClick={() => onCreateNewField(fieldName)}
      />
    </StyledGridRow>
  );
};
